// BillingFlagReasonContext.tsx
import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';

import AppContext from '@/contexts/appContext';
import ajax from '@/utils/ajax';

import { ReasonsByType } from '../types';

interface BillingFlagReasonContextType {
  billingItemFlagReasons: ReasonsByType;
  loading: boolean;
  error: string | null;
}

const BillingFlagReasonContext = createContext<BillingFlagReasonContextType>({
  billingItemFlagReasons: {},
  loading: false,
  error: null
});

interface BillingFlagReasonProviderProps {
  children: React.ReactNode;
}

const BillingFlagReasonProvider: React.FC<BillingFlagReasonProviderProps> = ({ children }) => {
  const { host } = useContext(AppContext);

  const [billingItemFlagReasons, setBillingItemFlagReasons] = useState<ReasonsByType>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBillingItemFlagReasons = useCallback(async () => {
    setLoading(true);
    try {
      const response = await ajax.get({
        url: `${host}/api/billing/billing_item_flag_reasons/`
      });

      if (response.status === 200) {
        setBillingItemFlagReasons(response.data);
      } else {
        setError('Failed to fetch billing item flag reasons');
      }
    } catch (error) {
      console.error('Error fetching billing item flag reasons: ', error);
      setError('Error fetching billing item flag reasons');
    } finally {
      setLoading(false);
    }
  }, [host]);

  useEffect(() => {
    fetchBillingItemFlagReasons();
  }, [fetchBillingItemFlagReasons]);

  return (
    <BillingFlagReasonContext.Provider value={{ billingItemFlagReasons, loading, error }}>
      {children}
    </BillingFlagReasonContext.Provider>
  );
};

export { BillingFlagReasonContext, BillingFlagReasonProvider };
